import IcImage from '@/assets/icons/ic-image.svg';
import IcReorder from '@/assets/icons/ic-reorder.svg';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils/cn';
import { PageStatus } from '@/types/store';
import type { FunnelPageRelationship } from '../../../../logic';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../../types';
import { FunnelPageActionsMenu } from '../../../shared/FunnelPageActionsMenu';
import { branchTagFor, getRoleLabel } from './manageFunnelPages.logic';

interface ManageRowProps {
    page: FunnelPageItem;
    stepNumber?: number;
    parentStepNumber?: number;
    productInfo: FunnelPageProductInfo | undefined;
    relationship: FunnelPageRelationship | undefined;
    isNested?: boolean;
    hideDragHandle?: boolean;
    isAncestorHidden?: boolean;
    isNew?: boolean;
    onHide: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
}

export const ManageRow = ({
    page,
    stepNumber,
    parentStepNumber,
    productInfo,
    relationship,
    isNested = false,
    hideDragHandle = false,
    isAncestorHidden = false,
    isNew = false,
    onHide,
    onDuplicate,
    onDelete,
}: ManageRowProps) => {
    const title = productInfo?.title || page.title || 'Untitled Product';
    const priceLabel =
        typeof productInfo?.price === 'number' && productInfo.price > 0
            ? formatCurrency(productInfo.price, productInfo.currency ?? 'USD')
            : '';
    const isHidden = page.status === PageStatus.DRAFT;
    const isDimmed = isHidden || isAncestorHidden;
    const branchTag = isNested ? branchTagFor(relationship) : null;
    const subtitle = !isNested ? getRoleLabel(relationship) : null;

    return (
        <div
            className={cn(
                'hover:bg-neutral-dimmed flex w-full items-center gap-2 rounded-lg px-2 py-2 transition-colors',
                isDimmed && 'opacity-60',
                isNew && 'animate-bounce-in'
            )}
        >
            {!hideDragHandle && (
                <span className='text-light flex size-6 shrink-0 cursor-grab items-center justify-center active:cursor-grabbing'>
                    <IcReorder className='size-4' />
                </span>
            )}

            {stepNumber !== undefined && (
                <span className='text-caption-2-bold text-gray w-4 shrink-0 text-center tabular-nums'>
                    {stepNumber}
                </span>
            )}

            <div
                className={cn(
                    'bg-canvas text-brand-main text-caption-1-bold border-neutral-subtle flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border',
                    isHidden && 'border-dashed'
                )}
            >
                {productInfo?.image ? (
                    <img src={productInfo.image} alt='' className='size-full object-cover' />
                ) : (
                    <IcImage className='text-gray size-5' />
                )}
            </div>

            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <div className='flex min-w-0 items-center gap-1.5'>
                    {isHidden ? (
                        <span className='bg-neutral-subtle text-gray text-caption-2-bold shrink-0 rounded px-1.5 py-px uppercase'>
                            Hidden
                        </span>
                    ) : branchTag ? (
                        <>
                            <span className={cn('text-label-xs-bold! font-extrabold uppercase', branchTag.colorClass)}>
                                {branchTag.label}
                            </span>
                            {parentStepNumber !== undefined && (
                                <span className='text-caption-2-bold text-gray shrink-0'>
                                    from Page {parentStepNumber}
                                </span>
                            )}
                        </>
                    ) : subtitle ? (
                        <span className='text-caption-2-bold text-gray truncate uppercase'>{subtitle}</span>
                    ) : null}
                </div>
                <span className='text-heading-h5 text-dark truncate'>{title}</span>
            </div>

            {priceLabel && <span className='text-label-sm-bold text-dark shrink-0 pl-1'>{priceLabel}</span>}

            <div
                className='flex shrink-0 items-center justify-center'
                onClick={e => e.stopPropagation()}
                onPointerDown={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
            >
                <FunnelPageActionsMenu
                    pageId={page.page_id}
                    pageStatus={page.status}
                    pageType={page.type}
                    onHide={onHide}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                />
            </div>
        </div>
    );
};
